'use client';

import React, { useState } from 'react';
import { AppShell, Burger, Group, Text, Button, Container, UnstyledButton, Avatar, Menu, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { 
  IconDashboard,
  IconWallet,
  IconChartLine,
  IconExchange,
  IconBuildingBank,
  IconHistory,
  IconShield,
  IconUsersGroup,
  IconBell,
  IconSettings, 
  IconChevronDown,
  IconUser,
  IconLogout,
  IconCoins,
  IconTrendingUp,
  IconTransfer,
  IconReportAnalytics
} from '@tabler/icons-react';

interface NavigationItem {
  label: string;
  href: string;
  icon: React.ElementType;
  children?: NavigationItem[];
}

const navigation: NavigationItem[] = [
  { 
    label: 'Dashboard', 
    href: '/dashboard', 
    icon: IconDashboard 
  },
  { 
    label: 'Portfolio', 
    href: '/dashboard/portfolio', 
    icon: IconWallet,
    children: [
      { label: 'All Assets', href: '/dashboard/portfolio', icon: IconWallet },
      { label: 'Crypto', href: '/dashboard/portfolio/crypto', icon: IconCoins },
      { label: 'Traditional', href: '/dashboard/portfolio/traditional', icon: IconBuildingBank },
    ]
  },
  { 
    label: 'Trade', 
    href: '/dashboard/trade', 
    icon: IconExchange 
  },
  { 
    label: 'Markets', 
    href: '/dashboard/markets', 
    icon: IconTrendingUp 
  },
  { 
    label: 'Exchanges & Transfers', 
    href: '/dashboard/exchanges', 
    icon: IconTransfer,
    children: [
      { label: 'Exchanges', href: '/dashboard/exchanges', icon: IconBuildingBank },
      { label: 'Transfers', href: '/dashboard/transfers', icon: IconTransfer },
    ]
  },
  { 
    label: 'Transactions', 
    href: '/dashboard/transactions', 
    icon: IconHistory 
  },
  { 
    label: 'Risk & Analytics', 
    href: '/dashboard/analytics', 
    icon: IconReportAnalytics,
    children: [
      { label: 'Risk Dashboard', href: '/dashboard/risk', icon: IconShield },
      { label: 'Performance', href: '/dashboard/analytics', icon: IconChartLine },
    ]
  },
  { 
    label: 'Family Office', 
    href: '/dashboard/family', 
    icon: IconUsersGroup 
  },
  { 
    label: 'Alerts', 
    href: '/dashboard/alerts', 
    icon: IconBell 
  },
  { 
    label: 'Settings', 
    href: '/dashboard/settings', 
    icon: IconSettings 
  },
];

export function AppShellComponent({ children }: { children: React.ReactNode }) {
  const [opened, { toggle }] = useDisclosure();
  const pathname = usePathname();

  const isLandingPage = pathname === '/';

  const NavigationItem = ({ item }: { item: NavigationItem }) => {
    const Icon = item.icon;
    const isActive = pathname === item.href || (item.children && item.children.some(child => pathname === child.href));
    const [expanded, setExpanded] = useState(false);

    // Auto-expand if current path matches a child
    React.useEffect(() => {
      if (item.children && item.children.some(child => pathname === child.href)) {
        setExpanded(true);
      }
    }, [pathname, item.children]);

    if (item.children) {
      return (
        <div>
          <UnstyledButton
            onClick={() => setExpanded(!expanded)}
            style={{
              display: 'block',
              width: '100%',
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: isActive ? 'var(--mantine-color-blue-light)' : 'transparent',
              color: isActive ? 'var(--mantine-color-blue-filled)' : 'inherit',
              marginBottom: '4px',
              transition: 'all 200ms ease',
            }}
          >
            <Group justify="space-between">
              <Group gap="sm">
                <Icon size={20} stroke={1.5} />
                <Text size="sm" fw={500}>{item.label}</Text>
              </Group>
              <IconChevronDown 
                size={16} 
                style={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms ease'
                }} 
              />
            </Group>
          </UnstyledButton>
          {expanded && (
            <div style={{ marginLeft: '16px', marginBottom: '8px' }}>
              {item.children.map((child) => (
                <UnstyledButton
                  key={child.href}
                  component={Link}
                  href={child.href}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    backgroundColor: pathname === child.href ? 'var(--mantine-color-blue-light)' : 'transparent',
                    color: pathname === child.href ? 'var(--mantine-color-blue-filled)' : 'inherit',
                    marginBottom: '2px',
                    transition: 'all 200ms ease',
                  }}
                >
                  <Group gap="sm">
                    <child.icon size={16} stroke={1.5} />
                    <Text size="sm">{child.label}</Text>
                  </Group>
                </UnstyledButton>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <UnstyledButton
        component={Link}
        href={item.href}
        style={{
          display: 'block',
          width: '100%',
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: isActive ? 'var(--mantine-color-blue-light)' : 'transparent',
          color: isActive ? 'var(--mantine-color-blue-filled)' : 'inherit',
          marginBottom: '4px',
          transition: 'all 200ms ease',
        }}
      >
        <Group justify="flex-start" gap="sm">
          <Icon size={20} stroke={1.5} />
          <Text size="sm" fw={500}>{item.label}</Text>
        </Group>
      </UnstyledButton>
    );
  };

  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{ width: 300, breakpoint: 'sm', collapsed: { mobile: !opened, desktop: isLandingPage } }}
      padding="md"
    >
      <AppShell.Header>
        <Container size="xl" h="100%">
          <Group h="100%" justify="space-between">
            <Group>
              {!isLandingPage && (
                <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
              )}
              <Link href="/" style={{ textDecoration: 'none' }}>
                <Text size="xl" fw={700} c="blue">
                  Family Office Platform
                </Text>
              </Link>
            </Group>

            <Group gap="lg">
              {isLandingPage ? (
                <>
                  <Button variant="subtle" component={Link} href="/signin" size="lg">
                    Sign In
                  </Button>
                  <Button component={Link} href="/register" size="lg">
                    Get Started
                  </Button>
                </>
              ) : (
                <>
                  <Box>
                    <Text size="sm" c="dimmed">Portfolio Value</Text>
                    <Text size="lg" fw={700} c="teal">$2,848,680</Text>
                  </Box>
                  <Menu width={300} position="bottom-end">
                    <Menu.Target>
                      <UnstyledButton style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                        <Group gap="sm">
                          <Avatar size={32} radius="xl" color="blue">
                            <Text size="sm" fw={700}>JD</Text>
                          </Avatar>
                          <div>
                            <Text size="sm" fw={600}>
                              John Doe
                            </Text>
                            <Text size="xs" c="dimmed">
                              Family Office
                            </Text>
                          </div>
                          <IconChevronDown size={14} stroke={1.5} />
                        </Group>
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconUser size={16} />}>
                        Profile Settings
                      </Menu.Item>
                      <Menu.Item leftSection={<IconSettings size={16} />}>
                        System Settings
                      </Menu.Item>
                      <Menu.Item leftSection={<IconBell size={16} />}>
                        Notifications
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item color="red" leftSection={<IconLogout size={16} />}>
                        Sign Out
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </>
              )}
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      {!isLandingPage && (
        <AppShell.Navbar p="md">
          <Text size="lg" fw={600} mb="md" c="blue">
            Navigation
          </Text>
          {navigation.map((item) => (
            <NavigationItem key={item.href} item={item} />
          ))}
        </AppShell.Navbar>
      )}

      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
}